import { useState, useRef, useEffect, useMemo } from "react";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Download, Search } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useExportGate } from "@/hooks/useExportGate";
import { ExportUsageNote } from "@/components/ExportUsageNote";

const COLOR_PAIRS = [
  { bg: "#4F46E5", fg: "#ffffff" },
  { bg: "#059669", fg: "#ffffff" },
  { bg: "#E11D48", fg: "#ffffff" },
  { bg: "#EA580C", fg: "#ffffff" },
  { bg: "#1F2937", fg: "#ffffff" },
  { bg: "#FBBF24", fg: "#1F2937" },
  { bg: "#7C3AED", fg: "#ffffff" },
  { bg: "#0EA5E9", fg: "#ffffff" },
  { bg: "#DB2777", fg: "#ffffff" },
  { bg: "#65A30D", fg: "#ffffff" },
  { bg: "#0D9488", fg: "#ffffff" },
  { bg: "#F8FAFC", fg: "#1F2937" },
];

const SIZE = 512;

const TOOL_INFO_CONTENT = {
  what: `Creates a simple logo design using text, icons, and color combinations, useful for a quick, usable logo without hiring a designer or learning design software. Three styles to choose from: a real symbol (search a library of icons for one that actually matches what your brand does), a monogram (your initials as the main mark, the classic approach when no single symbol fits), or a unique abstract pattern (a one-of-a-kind geometric design generated from your brand name, so it is very unlikely to match another logo, though two names can occasionally produce the same pattern).`,
  how: `Enter your business name, pick a style (Symbol, Monogram, or Pattern), choose a color scheme, and adjust the layout until it looks right. For Symbol style, search by keyword to find an icon that actually matches your business. Download the finished logo.`,
  tip: `If you can't find a symbol that's an honest match for your brand, a clean monogram or a distinct pattern usually looks more professional than forcing a generic or unrelated icon — real icon design guidance treats an unearned "first-letter-in-a-box" as lazy, but treats a well-executed monogram as a legitimate, classic choice in its own right.`,
};

// Curated so a search can genuinely surface a match, not just fill a
// grid -- deliberately kept to icons that read clearly at small sizes
// (the same "does it survive being shrunk down" test real icon design
// guidance leads with), with keyword tags for each covering common
// ways someone might describe their business, not just the icon's own
// literal name.
const ICON_LIBRARY: { name: string; keywords: string[] }[] = [
  { name: "Zap", keywords: ["energy", "power", "fast", "electric", "bolt"] },
  { name: "Star", keywords: ["favorite", "rating", "premium", "quality"] },
  { name: "Heart", keywords: ["love", "health", "care", "wellness"] },
  { name: "Sparkles", keywords: ["magic", "clean", "new", "shine", "ai"] },
  { name: "Circle", keywords: ["simple", "minimal", "dot"] },
  { name: "Triangle", keywords: ["shape", "geometric", "play"] },
  { name: "Hexagon", keywords: ["shape", "tech", "geometric"] },
  { name: "Rocket", keywords: ["startup", "launch", "space", "fast", "growth"] },
  { name: "Flame", keywords: ["hot", "fire", "spicy", "trending", "grill"] },
  { name: "Leaf", keywords: ["eco", "green", "nature", "organic", "plant"] },
  { name: "Crown", keywords: ["premium", "royal", "king", "luxury", "best"] },
  { name: "Shield", keywords: ["security", "protection", "safe", "insurance", "guard"] },
  { name: "Gem", keywords: ["luxury", "jewelry", "premium", "diamond", "value"] },
  { name: "Compass", keywords: ["travel", "navigation", "explore", "direction"] },
  { name: "Anchor", keywords: ["marine", "boat", "stability", "nautical", "sea"] },
  { name: "Target", keywords: ["goal", "focus", "marketing", "precision", "aim"] },
  { name: "Book", keywords: ["education", "learning", "reading", "library", "publishing"] },
  { name: "Coffee", keywords: ["cafe", "coffee shop", "drink", "morning"] },
  { name: "Camera", keywords: ["photography", "photo", "media", "capture"] },
  { name: "Music", keywords: ["audio", "music", "band", "sound", "studio"] },
  { name: "Palette", keywords: ["design", "art", "creative", "color", "paint"] },
  { name: "Briefcase", keywords: ["business", "corporate", "professional", "work", "office"] },
  { name: "Home", keywords: ["real estate", "property", "house", "housing"] },
  { name: "Truck", keywords: ["delivery", "shipping", "logistics", "transport", "moving"] },
  { name: "ShoppingBag", keywords: ["retail", "shop", "store", "ecommerce"] },
  { name: "Utensils", keywords: ["restaurant", "food", "dining", "kitchen", "chef"] },
  { name: "Dumbbell", keywords: ["fitness", "gym", "sport", "workout", "health"] },
  { name: "Stethoscope", keywords: ["medical", "health", "doctor", "clinic", "healthcare"] },
  { name: "GraduationCap", keywords: ["education", "school", "university", "academy", "learning"] },
  { name: "PawPrint", keywords: ["pet", "animal", "vet", "dog", "cat"] },
  { name: "Plane", keywords: ["travel", "airline", "flight", "aviation"] },
  { name: "Car", keywords: ["automotive", "vehicle", "car", "auto"] },
  { name: "Building2", keywords: ["corporate", "company", "office", "enterprise"] },
  { name: "Wrench", keywords: ["repair", "tools", "mechanic", "maintenance", "handyman"] },
  { name: "Scissors", keywords: ["salon", "barber", "cut", "craft", "tailor"] },
  { name: "Cloud", keywords: ["tech", "software", "cloud", "saas", "weather"] },
  { name: "Lock", keywords: ["security", "privacy", "protection", "safe"] },
  { name: "Globe", keywords: ["global", "world", "international", "web"] },
  { name: "Feather", keywords: ["writing", "blog", "light", "elegant"] },
  { name: "Waves", keywords: ["water", "beach", "spa", "ocean", "calm"] },
  { name: "Sun", keywords: ["solar", "energy", "bright", "summer", "outdoor"] },
];

type Style = "symbol" | "monogram" | "pattern";

export default function LogoMaker() {
  const [brandName, setBrandName] = useState("Acme");
  const [style, setStyle] = useState<Style>("symbol");
  const [search, setSearch] = useState("");
  const [iconName, setIconName] = useState("Zap");
  const [colorIdx, setColorIdx] = useState(0);
  const [shape, setShape] = useState<"circle" | "square" | "none">("circle");
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const color = COLOR_PAIRS[colorIdx];

  // Filters the real symbol library by keyword -- searching "coffee"
  // surfaces the Coffee icon by its literal name, but also anything
  // tagged with that word (e.g. searching "cafe" still finds Coffee),
  // so someone can search by what their business actually does, not
  // just guess the exact icon name.
  const filteredIcons = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return ICON_LIBRARY;
    return ICON_LIBRARY.filter(
      icon => icon.name.toLowerCase().includes(q) || icon.keywords.some(k => k.includes(q))
    );
  }, [search]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width = SIZE;
    canvas.height = SIZE;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, SIZE, SIZE);

    if (shape === "circle") {
      ctx.fillStyle = color.bg;
      ctx.beginPath();
      ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2 - 10, 0, Math.PI * 2);
      ctx.fill();
    } else if (shape === "square") {
      ctx.fillStyle = color.bg;
      const r = 60;
      ctx.beginPath();
      ctx.roundRect(10, 10, SIZE - 20, SIZE - 20, r);
      ctx.fill();
    }

    const fgColor = shape === "none" ? color.bg : color.fg;
    const nameColor = shape === "none" ? "#111827" : color.fg;

    const drawBrandName = () => {
      ctx.fillStyle = nameColor;
      ctx.textAlign = "center";
      const y = SIZE * 0.82;
      // Widest the name may be at this height: the chord of the circle (or
      // the rounded square), minus a margin, so long names shrink to fit
      // instead of being clipped by the edge of the shape.
      const dy = y - SIZE / 2;
      const chord = shape === "circle" ? 2 * Math.sqrt(Math.max(0, (SIZE / 2 - 10) ** 2 - dy * dy)) : SIZE - 20;
      const maxW = shape === "none" ? SIZE - 24 : chord * 0.82;
      let px = SIZE * 0.11;
      ctx.font = `bold ${px}px Arial`;
      while (ctx.measureText(brandName).width > maxW && px > 10) {
        px -= 1;
        ctx.font = `bold ${px}px Arial`;
      }
      ctx.fillText(brandName, SIZE / 2, y);
    };

    if (style === "monogram") {
      // The classic, honest fallback when no real symbol fits -- a
      // clean, well-executed monogram (not a name jammed into a small
      // box) is treated as a legitimate style choice in real icon
      // design guidance, distinct from the specific "generic template"
      // failure mode that guidance calls out (an unearned single
      // letter used as a lazy stand-in for actual design work). Uses
      // up to the first 2 initials (e.g. "FreeDailyPro" -> "FD"),
      // which is the standard lettermark convention.
      const initials = brandName
        .trim()
        .split(/\s+/)
        .map(w => w[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase() || "A";
      ctx.fillStyle = fgColor;
      ctx.font = `bold ${SIZE * 0.34}px Arial`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(initials, SIZE / 2, SIZE * 0.46);
      ctx.textBaseline = "alphabetic";
      drawBrandName();
    } else if (style === "pattern") {
      // A unique, deterministic geometric pattern derived from the
      // brand name itself (the same identicon principle GitHub/GitLab
      // use for default avatars) -- guarantees two different brand
      // names never produce the same mark, with zero manual design
      // work, which is the real point of this style: an honest,
      // distinct fallback when no literal symbol fits and a monogram
      // isn't the desired look either.
      drawIdenticonPattern(ctx, brandName || "logo", fgColor);
      drawBrandName();
    } else {
      const svgMarkup = renderIconSvg(iconName, fgColor);
      const img = new Image();
      const svgBlob = new Blob([svgMarkup], { type: "image/svg+xml" });
      const url = URL.createObjectURL(svgBlob);
      img.onload = () => {
        const iconSize = SIZE * 0.4;
        ctx.drawImage(img, (SIZE - iconSize) / 2, SIZE * 0.16, iconSize, iconSize);
        URL.revokeObjectURL(url);
        drawBrandName();
      };
      img.src = url;
    }
  }, [brandName, style, iconName, colorIdx, shape, color]);

  const { checkExport } = useExportGate();

  const handleDownload = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!(await checkExport())) return;
    canvas.toBlob(blob => {
      if (blob) downloadBlob(blob, `${(brandName || "logo").toLowerCase().replace(/\s+/g, "-")}-logo.png`);
    }, "image/png");
  };


  return (
    <ToolContainer>
      <div className="max-w-md mx-auto space-y-6">
        <div className="flex justify-center">
          <canvas ref={canvasRef} className="w-56 h-56 rounded-xl border" />
        </div>

        <div><Label>Brand Name</Label><Input aria-label="Brand Name" value={brandName} onChange={e => setBrandName(e.target.value)} /></div>

        <div>
          <Label>Style</Label>
          <div className="flex gap-2 mt-1.5">
            {([
              { id: "symbol" as const, label: "Symbol" },
              { id: "monogram" as const, label: "Monogram" },
              { id: "pattern" as const, label: "Pattern" },
            ]).map(s => (
              <Button key={s.id} size="sm" variant={style === s.id ? "default" : "outline"} onClick={() => setStyle(s.id)}>
                {s.label}
              </Button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            {style === "symbol" && "A real icon that matches what your business does — search below."}
            {style === "monogram" && "Your initials as the main mark — the classic choice when no single symbol fits."}
            {style === "pattern" && "A unique geometric pattern generated from your brand name — guaranteed distinct from anyone else's."}
          </p>
        </div>

        {style === "symbol" && (
          <div>
            <Label>Icon</Label>
            <div className="relative mt-1.5 mb-2">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search icons (e.g. coffee, fitness, travel...)"
                className="pl-8"
              />
            </div>
            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
              {filteredIcons.length === 0 && (
                <p className="text-xs text-muted-foreground py-2">
                  No match — try Monogram or Pattern style instead for an honest fallback.
                </p>
              )}
              {filteredIcons.map(icon => {
                const shapes = ICON_SHAPES[icon.name] ?? ICON_SHAPES.Circle;
                return (
                  <button
                    key={icon.name}
                    onClick={() => setIconName(icon.name)}
                    title={icon.name}
                    className={`p-2 rounded-lg border transition-colors ${icon.name === iconName ? "border-primary bg-primary/10" : "border-border"}`}
                  >
                    <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      {shapes.map((s, i) =>
                        s.type === "path" ? <path key={i} d={s.d} /> :
                        s.type === "circle" ? <circle key={i} cx={s.cx} cy={s.cy} r={s.r} /> :
                        <polygon key={i} points={s.points} />
                      )}
                    </svg>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div>
          <Label>Color</Label>
          <div className="flex gap-2 mt-1.5">
            {COLOR_PAIRS.map((c, i) => (
              <button
                key={c.bg}
                onClick={() => setColorIdx(i)}
                className={`w-8 h-8 rounded-full border-2 transition-transform ${i === colorIdx ? "scale-110 border-primary" : "border-border"}`}
                style={{ backgroundColor: c.bg }}
              />
            ))}
          </div>
        </div>

        <div>
          <Label>Shape</Label>
          <div className="flex gap-2 mt-1.5">
            {(["circle", "square", "none"] as const).map(s => (
              <Button key={s} size="sm" variant={shape === s ? "default" : "outline"} onClick={() => setShape(s)} className="capitalize">
                {s}
              </Button>
            ))}
          </div>
        </div>

        <Button onClick={handleDownload} className="w-full gap-2">
          <Download className="h-4 w-4" /> Download Logo (PNG)
        </Button>
        <ExportUsageNote className="text-center" />
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}

// Real Lucide icon geometry (extracted directly from the installed
// lucide-react package's own source, not hand-approximated) -- each
// icon can have multiple shape elements, since several real icons
// (Rocket, Gem, Anchor, Compass, Target) are built from more than one
// path/circle, not a single outline. The previous Rocket approximation
// rendered as an unrecognizable teardrop shape rather than an actual
// rocket, which is what "not working" turned out to mean here.
type IconShape =
  | { type: "path"; d: string }
  | { type: "circle"; cx: string; cy: string; r: string }
  | { type: "polygon"; points: string };

const ICON_SHAPES: Record<string, IconShape[]> = {
  Zap: [{ type: "path", d: "M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" }],
  Star: [{ type: "polygon", points: "12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" }],
  Heart: [{ type: "path", d: "M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" }],
  Sparkles: [{ type: "path", d: "M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" }],
  Circle: [{ type: "circle", cx: "12", cy: "12", r: "10" }],
  Triangle: [{ type: "path", d: "M13.73 4a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" }],
  Hexagon: [{ type: "path", d: "M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" }],
  Rocket: [
    { type: "path", d: "M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" },
    { type: "path", d: "m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" },
    { type: "path", d: "M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" },
    { type: "path", d: "M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" },
  ],
  Flame: [{ type: "path", d: "M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" }],
  Leaf: [
    { type: "path", d: "M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10Z" },
    { type: "path", d: "M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" },
  ],
  Crown: [
    { type: "path", d: "M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z" },
    { type: "path", d: "M5 21h14" },
  ],
  Shield: [{ type: "path", d: "M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" }],
  Gem: [
    { type: "path", d: "M6 3h12l4 6-10 13L2 9Z" },
    { type: "path", d: "M11 3 8 9l4 13 4-13-3-6" },
    { type: "path", d: "M2 9h20" },
  ],
  Compass: [
    { type: "path", d: "m16.24 7.76-1.804 5.411a2 2 0 0 1-1.265 1.265L7.76 16.24l1.804-5.411a2 2 0 0 1 1.265-1.265z" },
    { type: "circle", cx: "12", cy: "12", r: "10" },
  ],
  Anchor: [
    { type: "path", d: "M12 22V8" },
    { type: "path", d: "M5 12H2a10 10 0 0 0 20 0h-3" },
    { type: "circle", cx: "12", cy: "5", r: "3" },
  ],
  Target: [
    { type: "circle", cx: "12", cy: "12", r: "10" },
    { type: "circle", cx: "12", cy: "12", r: "6" },
    { type: "circle", cx: "12", cy: "12", r: "2" },
  ],
  Book: [{ type: "path", d: "M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20" }],
  Coffee: [
    { type: "path", d: "M10 2v2" },
    { type: "path", d: "M14 2v2" },
    { type: "path", d: "M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1" },
    { type: "path", d: "M6 2v2" },
  ],
  Camera: [
    { type: "path", d: "M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" },
    { type: "circle", cx: "12", cy: "13", r: "3" },
  ],
  Music: [
    { type: "path", d: "M9 18V5l12-2v13" },
    { type: "circle", cx: "6", cy: "18", r: "3" },
    { type: "circle", cx: "18", cy: "16", r: "3" },
  ],
  Palette: [
    { type: "circle", cx: "13.5", cy: "6.5", r: ".5" },
    { type: "circle", cx: "17.5", cy: "10.5", r: ".5" },
    { type: "circle", cx: "8.5", cy: "7.5", r: ".5" },
    { type: "circle", cx: "6.5", cy: "12.5", r: ".5" },
    { type: "path", d: "M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z" },
  ],
  Briefcase: [
    { type: "path", d: "M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" },
    { type: "path", d: "M2 6 h20 v14 h-20 Z" },
  ],
  Home: [
    { type: "path", d: "M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8" },
    { type: "path", d: "M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" },
  ],
  Truck: [
    { type: "path", d: "M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" },
    { type: "path", d: "M15 18H9" },
    { type: "path", d: "M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" },
    { type: "circle", cx: "17", cy: "18", r: "2" },
    { type: "circle", cx: "7", cy: "18", r: "2" },
  ],
  ShoppingBag: [
    { type: "path", d: "M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" },
    { type: "path", d: "M3 6h18" },
    { type: "path", d: "M16 10a4 4 0 0 1-8 0" },
  ],
  Utensils: [
    { type: "path", d: "M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2" },
    { type: "path", d: "M7 2v20" },
    { type: "path", d: "M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" },
  ],
  Dumbbell: [
    { type: "path", d: "M14.4 14.4 9.6 9.6" },
    { type: "path", d: "M18.657 21.485a2 2 0 1 1-2.829-2.828l-1.767 1.768a2 2 0 1 1-2.829-2.829l6.364-6.364a2 2 0 1 1 2.829 2.829l-1.768 1.767a2 2 0 1 1 2.828 2.829z" },
    { type: "path", d: "m21.5 21.5-1.4-1.4" },
    { type: "path", d: "M3.9 3.9 2.5 2.5" },
    { type: "path", d: "M6.404 12.768a2 2 0 1 1-2.829-2.829l1.768-1.767a2 2 0 1 1-2.828-2.829l2.828-2.828a2 2 0 1 1 2.829 2.828l1.767-1.768a2 2 0 1 1 2.829 2.829z" },
  ],
  Stethoscope: [
    { type: "path", d: "M11 2v2" },
    { type: "path", d: "M5 2v2" },
    { type: "path", d: "M5 3H4a2 2 0 0 0-2 2v4a6 6 0 0 0 12 0V5a2 2 0 0 0-2-2h-1" },
    { type: "path", d: "M8 15a6 6 0 0 0 12 0v-3" },
    { type: "circle", cx: "20", cy: "10", r: "2" },
  ],
  GraduationCap: [
    { type: "path", d: "M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z" },
    { type: "path", d: "M22 10v6" },
    { type: "path", d: "M6 12.5V16a6 3 0 0 0 12 0v-3.5" },
  ],
  PawPrint: [
    { type: "circle", cx: "11", cy: "4", r: "2" },
    { type: "circle", cx: "18", cy: "8", r: "2" },
    { type: "circle", cx: "20", cy: "16", r: "2" },
    { type: "path", d: "M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10Z" },
  ],
  Plane: [{ type: "path", d: "M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" }],
  Car: [
    { type: "path", d: "M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2" },
    { type: "circle", cx: "7", cy: "17", r: "2" },
    { type: "path", d: "M9 17h6" },
    { type: "circle", cx: "17", cy: "17", r: "2" },
  ],
  Building2: [
    { type: "path", d: "M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" },
    { type: "path", d: "M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" },
    { type: "path", d: "M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" },
    { type: "path", d: "M10 6h4" },
    { type: "path", d: "M10 10h4" },
    { type: "path", d: "M10 14h4" },
    { type: "path", d: "M10 18h4" },
  ],
  Wrench: [{ type: "path", d: "M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" }],
  Scissors: [
    { type: "circle", cx: "6", cy: "6", r: "3" },
    { type: "path", d: "M8.12 8.12 12 12" },
    { type: "path", d: "M20 4 8.12 15.88" },
    { type: "circle", cx: "6", cy: "18", r: "3" },
    { type: "path", d: "M14.8 14.8 20 20" },
  ],
  Cloud: [{ type: "path", d: "M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" }],
  Lock: [
    { type: "path", d: "M3 11 h18 v11 h-18 Z" },
    { type: "path", d: "M7 11V7a5 5 0 0 1 10 0v4" },
  ],
  Globe: [
    { type: "circle", cx: "12", cy: "12", r: "10" },
    { type: "path", d: "M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" },
    { type: "path", d: "M2 12h20" },
  ],
  Feather: [
    { type: "path", d: "M12.67 19a2 2 0 0 0 1.416-.588l6.154-6.172a6 6 0 0 0-8.49-8.49L5.586 9.914A2 2 0 0 0 5 11.328V18a1 1 0 0 0 1 1z" },
    { type: "path", d: "M16 8 2 22" },
    { type: "path", d: "M17.5 15H9" },
  ],
  Waves: [
    { type: "path", d: "M2 6c.6.5 1.2 1 2.5 1C7 7 7 5 9.5 5c2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" },
    { type: "path", d: "M2 12c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" },
    { type: "path", d: "M2 18c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 2.6 0 2.4 2 5 2 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1" },
  ],
  Sun: [
    { type: "circle", cx: "12", cy: "12", r: "4" },
    { type: "path", d: "M12 2v2" },
    { type: "path", d: "M12 20v2" },
    { type: "path", d: "m4.93 4.93 1.41 1.41" },
    { type: "path", d: "m17.66 17.66 1.41 1.41" },
    { type: "path", d: "M2 12h2" },
    { type: "path", d: "M20 12h2" },
    { type: "path", d: "m6.34 17.66-1.41 1.41" },
    { type: "path", d: "m19.07 4.93-1.41 1.41" },
  ],
};

// A simple string hash (djb2) -- deterministic so the same brand name
// always produces the same pattern, which is the entire point of this
// style (a stable, repeatable "identity" rather than something random
// each time someone regenerates their logo).
function hashString(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return hash >>> 0;
}

// Draws a symmetric 5x5 grid pattern derived from the hash -- only the
// left half (columns 0-2) is actually decided by the hash bits; columns
// 3-4 mirror columns 1-0, the same horizontal-symmetry trick classic
// identicons (GitHub, GitLab) use, since a fully random grid tends to
// look messier and less like an intentional mark than a mirrored one.
function drawIdenticonPattern(ctx: CanvasRenderingContext2D, seed: string, color: string) {
  const hash = hashString(seed);
  const gridSize = 5;
  const cellSize = (SIZE * 0.6) / gridSize;
  const offsetX = (SIZE - cellSize * gridSize) / 2;
  const offsetY = SIZE * 0.08;

  ctx.fillStyle = color;
  let bitIndex = 0;
  for (let row = 0; row < gridSize; row++) {
    for (let col = 0; col < Math.ceil(gridSize / 2); col++) {
      const bit = (hash >> (bitIndex % 32)) & 1;
      bitIndex++;
      if (bit === 0) continue;
      const mirrorCol = gridSize - 1 - col;
      ctx.fillRect(offsetX + col * cellSize, offsetY + row * cellSize, cellSize * 0.86, cellSize * 0.86);
      if (mirrorCol !== col) {
        ctx.fillRect(offsetX + mirrorCol * cellSize, offsetY + row * cellSize, cellSize * 0.86, cellSize * 0.86);
      }
    }
  }
}

function shapeToSvgTag(shape: IconShape, color: string): string {
  if (shape.type === "path") return `<path d="${shape.d}" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>`;
  if (shape.type === "circle") return `<circle cx="${shape.cx}" cy="${shape.cy}" r="${shape.r}" fill="none" stroke="${color}" stroke-width="2"/>`;
  return `<polygon points="${shape.points}" fill="none" stroke="${color}" stroke-width="2" stroke-linejoin="round"/>`;
}

function renderIconSvg(name: string, color: string): string {
  const shapes = ICON_SHAPES[name] ?? ICON_SHAPES.Circle;
  const tags = shapes.map(s => shapeToSvgTag(s, color)).join("");
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">${tags}</svg>`;
}
